import { useTranslation } from "i18n"
import metadata from "metadata.json"
import Link from "next/link"
import { useCallback, useEffect, useRef, useState } from "react"

// Material UI
import {
  Box,
  Divider,
  Grid,
  Hidden,
  Paper,
  Typography
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"

// Components
import Button from "components/shared/Button"
import StoreLoader from "components/shared/StoreLoader"
import Filters from "components/store/Filters"
import RectangleCard from "components/store/RectangleCard"
import SimpleCard from "components/store/SimpleCard"
import StoreContainer from "components/store/StoreContainer"

// Icons
import FilterListIcon from "@material-ui/icons/FilterList"

// Helpers
import axios from "shared/axios"
import { getProductPageLink } from "shared/helpersTs"
import Metadata from "shared/metadata"
import urls from "shared/urls"
const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3)
  },
  typography: {
    display: "flex",
    alignItems: "center",
    textTransform: "capitalize"
  },
  icon: {
    marginRight: theme.spacing(0.5)
  },
  yellow: {
    color: theme.palette.warning.main
  },
  gridTransition: {
    transition: theme.transitions.create("all", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  imageContainer: {
    width: 100,
    maxHeight: 30
  },
  img: {
    margin: "auto",
    display: "block",
    maxWidth: "100%",
    maxHeight: "100%"
  },
  fontWeightBolder: {
    fontWeight: theme.typography.fontWeightBolder
  }
}))

export default function Qualifications() {
  const { t } = useTranslation()

  const classes = useStyles()

  const [qualifications, setQualifications] = useState(null)
  const [totalQualifications, setTotalQualifications] = useState(null)

  // Filters
  const [filters, setFilters] = useState({
    language: [],
    level: [],
    learning_mode: []
  })
  const [filterOpen, setFilterOpen] = useState(false)
  const [levelList, setLevelList] = useState([])
  const [learningModeList, setLearningModeList] = useState([])
  const [languageList, setLanguageList] = useState([])
  const [page, setPage] = useState(1)

  const [pageLoad, setPageLoad] = useState(true)
  const [hasMore, setHasMore] = useState(false)

  const observer = useRef()
  const lastElementRef = useCallback(
    node => {
      if (pageLoad) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          if (Math.ceil(totalQualifications / 10) > page) {
            setPage(prevPageNumber => prevPageNumber + 1)
          }
        }
      })
      if (node) observer.current.observe(node)
    },
    [pageLoad, hasMore, totalQualifications, page]
  )

  const getFilteredQualifications = e => {
    if (e.target.checked === true) {
      setPage(1)
      setQualifications(null)

      if (e.target.name === "learning_mode") {
        let list = [...learningModeList]
        list.push(e.target.id)
        setLearningModeList(list)
      }
      if (e.target.name === "language") {
        let list = [...languageList]
        list.push(e.target.id)
        setLanguageList(list)
      }

      if (e.target.name === "level") {
        let list = [...levelList]
        list.push(e.target.id)
        setLevelList(list)
      }
    }

    if (e.target.checked === false) {
      setPage(1)
      setQualifications(null)
      if (e.target.name === "learning_mode") {
        let list = [...learningModeList]
        list.splice(list.indexOf(e.target.id), 1)
        setLearningModeList(list)
      }
      if (e.target.name === "language") {
        let list = [...languageList]
        list.splice(list.indexOf(e.target.id), 1)
        setLanguageList(list)
      }

      if (e.target.name === "level") {
        let list = [...levelList]
        list.splice(list.indexOf(e.target.id), 1)
        setLevelList(list)
      }
    }
  }

  useEffect(() => {
    const getQualificationFilters = () => {
      axios
        .get(urls.store.qualifications.getFilters)
        .then(response => {
          setFilters(response.data.data)
        })
        .catch(console.error)
    }
    getQualificationFilters()
  }, [])

  useEffect(() => {
    const getQualifications = () => {
      setPageLoad(true)
      axios
        .get(urls.store.qualifications.list, {
          params: {
            fields: ["description", "duration"],
            language: languageList,
            learning_mode: learningModeList,
            level__in: levelList.join(","),
            page
          }
        })
        .then(response => {
          setTotalQualifications(response.data.total)

          setQualifications(prevQualifications => {
            if (prevQualifications === null) {
              return response.data.data
            } else {
              return [...prevQualifications, ...response.data.data]
            }
          })

          setPageLoad(false)
          setHasMore(response.data.data.length > 0)
        })
        .catch(console.error)
    }

    getQualifications()
  }, [languageList, learningModeList, levelList, page])
  return (
    <>
      <Metadata data={metadata["qualifications"]} />
      <StoreContainer>
        <Grid container spacing={4} direction="column">
          <Grid item>
            <Typography variant="h5" component="h1">
              {t("Qualifications")}
            </Typography>
            <Divider />
          </Grid>

          <Hidden smUp>
            <Grid item>
              <Button
                onClick={() => setFilterOpen(!filterOpen)}
                variant="outlined"
                color="primary"
              >
                <FilterListIcon />
                {t("Filter")}
              </Button>
            </Grid>

            {filterOpen && (
              <Grid item>
                <Filters
                  loading={qualifications === null}
                  filters={filters}
                  handleFilters={getFilteredQualifications}
                />
              </Grid>
            )}
          </Hidden>
        </Grid>

        <Box mt={3}>
          <Grid container spacing={3}>
            <Hidden xsDown>
              <Grid item xs={4} sm={3}>
                <Filters
                  loading={qualifications === null}
                  filters={filters}
                  handleFilters={getFilteredQualifications}
                />
              </Grid>
            </Hidden>
            <Grid item xs={12} sm={9}>
              <Grid container spacing={2}>
                {qualifications && qualifications.length === 0 && (
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Typography variant="h6">
                        {t("No qualifications found")}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {qualifications &&
                  qualifications.length > 0 &&
                  qualifications.map((qualification, index) => (
                    <Link
                      href={getProductPageLink(
                        qualification.slug,
                        `/qualifications/${qualification.slug}`
                      )}
                      key={qualification.uuid}
                    >
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={12}
                        ref={
                          qualifications.length === index + 1
                            ? lastElementRef
                            : null
                        }
                      >
                        <Hidden smDown>
                          <RectangleCard
                            name={qualification.name}
                            image={qualification.image}
                            description={qualification.description}
                            // Price
                            // prices={qualification.prices}
                            // Icons
                            number_of_enrollments={
                              qualification.number_of_enrollments
                            }
                            rating={qualification.rating}
                            level={qualification.qualification_level}
                            number_of_items={qualification.number_of_courses}
                            duration={qualification.duration}
                            isEnrolled={qualification.is_enrolled}
                          />
                        </Hidden>
                        <Hidden mdUp>
                          <SimpleCard
                            name={qualification.name}
                            image={qualification.image}
                            isEnrolled={qualification.is_enrolled}
                            // Price
                            // prices={qualification.prices}
                            // Icons
                            number_of_enrollments={
                              qualification.number_of_enrollments
                            }
                            rating={qualification.rating}
                            level={qualification.qualification_level}
                            number_of_items={qualification.number_of_courses}
                          />
                        </Hidden>
                      </Grid>
                    </Link>
                  ))}
                {pageLoad && (
                  <Grid item xs={12}>
                    <StoreLoader />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </StoreContainer>
    </>
  )
}
